/**
 * Vote reasons are stored uniformly in the API; the frontend distinguishes
 * tenant-configured presets from free-text comments by string comparison.
 */

export function getVoteReasonLabel(voteReason) {
  if (typeof voteReason === 'string') {
    return voteReason;
  }
  return voteReason?.reason ?? '';
}

export function isTenantVoteReason(reasonLabel, tenantVoteReasons) {
  if (!reasonLabel || !tenantVoteReasons?.length) {
    return false;
  }
  return tenantVoteReasons.some((presetReason) => presetReason.reason === reasonLabel);
}

export function getVoteReasonStrings(opportunityVote) {
  if (opportunityVote?.reasons?.length) {
    return [...opportunityVote.reasons];
  }
  if (opportunityVote?.transformedReasons?.length) {
    return opportunityVote.transformedReasons.map(getVoteReasonLabel);
  }
  return [];
}

export function partitionVoteReasons(opportunityVote, tenantVoteReasons) {
  const presetReasons = [];
  const customReasons = [];

  getVoteReasonStrings(opportunityVote).forEach((reasonLabel) => {
    if (isTenantVoteReason(reasonLabel, tenantVoteReasons)) {
      presetReasons.push(reasonLabel);
    } else {
      customReasons.push(reasonLabel);
    }
  });

  return { presetReasons, customReasons };
}

export function hasVoteReasons(opportunityVote) {
  return getVoteReasonStrings(opportunityVote).length > 0;
}

/** Turns stored "\\n" sequences into real line breaks for display. */
export function normalizeVoteReasonNewlines(text) {
  if (!text) {
    return '';
  }
  return String(text).replace(/\\n/g, '\n');
}

/**
 * Order is ignored because re-toggling a tag, or the API returning a different order, is not an
 * edit. Both sides are newline-normalised because the dialogs normalise on load, so a raw stored
 * value would otherwise look changed.
 */
export function sameVoteReasons(a = [], b = []) {
  if ((a || []).length !== (b || []).length) {
    return false;
  }
  const normalize = (list) => list.map(normalizeVoteReasonNewlines).sort();
  const left = normalize(a || []);
  const right = normalize(b || []);
  return left.every((value, index) => value === right[index]);
}

export function formatCustomVoteReasons(customReasons) {
  const text = customReasons.map(normalizeVoteReasonNewlines).join('\n');
  return text || null;
}
